import { useCallback, useState } from 'react';
import { Button, Input, message, Form } from 'antd';
import { Formik } from 'formik';
import { useTranslate } from '@tolgee/react';
import { LockKeyhole, CheckCircle, Sparkles } from 'lucide-react';
import { JustRightHeightModal } from 'components/styled/scoped';
import FormField from 'components/FormField';

const { TextArea } = Input;

export interface FeedbackFormValues {
  /** 优化建议描述 */
  suggestion: string;
  /** 建议回复内容 */
  suggestedReply: string;
}

export interface FeedbackModalProps {
  visible: boolean;
  onClose: () => void;
  /** 预设回复内容 */
  presetReply: string;
  /** 提交回调 */
  onSubmit?: (values: FeedbackFormValues) => Promise<void>;
}

// Mock 提交函数
const mockSubmit = async (values: FeedbackFormValues): Promise<void> => {
  console.log('Feedback submitted:', values);
  await new Promise((resolve) => setTimeout(resolve, 500));
};

const initialValues: FeedbackFormValues = {
  suggestion: '',
  suggestedReply: '',
};

function FeedbackModal(props: FeedbackModalProps) {
  const { t } = useTranslate();
  const { visible, onClose, presetReply, onSubmit = mockSubmit } = props;
  const [submitted, setSubmitted] = useState(false);

  const validate = useCallback(
    (values: FeedbackFormValues) => {
      const errors: Partial<Record<keyof FeedbackFormValues, string>> = {};
      if (!values.suggestion || !values.suggestion.trim()) {
        errors.suggestion = t('scene.aiAgentConfig.feedbackDialog.validation.suggestionRequired');
      }
      return errors;
    },
    [t],
  );

  const handleClose = (resetForm?: () => void) => {
    setSubmitted(false);
    resetForm?.();
    onClose();
  };

  return (
    <Formik
      initialValues={initialValues}
      validate={validate}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await onSubmit(values);
          setSubmitted(true);
          message.success(t('scene.aiAgentConfig.feedbackDialog.feedback.submitSuccess'));
        } catch {
          message.error(t('scene.aiAgentConfig.feedbackDialog.feedback.submitFailed'));
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ handleSubmit, isSubmitting, resetForm }) => (
        <JustRightHeightModal
          data-sid="fm-modal-01"
          title={t('scene.aiAgentConfig.feedbackDialog.title')}
          visible={visible}
          onCancel={() => handleClose(resetForm)}
          width={560}
          destroyOnClose
          footer={
            submitted ? null : (
              <>
                <Button data-sid="fm-cancelbtn-11" onClick={() => handleClose(resetForm)}>
                  {t('common.action.cancel')}
                </Button>
                <Button
                  data-sid="fm-submitbtn-12"
                  type="primary"
                  loading={isSubmitting}
                  onClick={() => handleSubmit()}
                >
                  {t('scene.aiAgentConfig.action.submit')}
                </Button>
              </>
            )
          }
        >
          {submitted ? (
            <div data-sid="fm-success-02" className="py-8 text-center">
              <div className="flex items-center justify-center gap-2 text-[#52C41A]">
                <CheckCircle size={20} />
                <span className="text-base">
                  {t('scene.aiAgentConfig.feedbackDialog.hint.feedbackReceived')}
                </span>
                <Sparkles size={16} className="text-[#FAAD14]" />
              </div>
            </div>
          ) : (
            <Form data-sid="fm-form-03" layout="vertical">
              {/* 预设回复展示区 */}
              <div
                data-sid="fm-preset-04"
                className="mb-4 p-3 rounded bg-[#F7F9FA] border border-[#F0F2F5]"
              >
                <div data-sid="fm-presethead-05" className="flex items-center gap-1 mb-2">
                  <LockKeyhole size={14} className="text-[#84868C]" />
                  <span className="text-sm text-[#84868C]">
                    {t('scene.aiAgentConfig.scenarioDetail.label.presetReply')}
                  </span>
                </div>
                <div data-sid="fm-presetcontent-06" className="text-sm text-[#515359] leading-5">
                  {presetReply}
                </div>
              </div>

              {/* 优化建议输入 */}
              <FormField<string>
                name="suggestion"
                label={
                  <span style={{ fontWeight: 500, fontSize: 14, color: '#232426' }}>
                    {t('scene.aiAgentConfig.feedbackDialog.label.suggestion')}
                  </span>
                }
                required
                data-sid="fm-suggestionfield-07"
              >
                {({ field }) => (
                  <TextArea
                    data-sid="fm-suggestioninput-08"
                    {...field}
                    rows={3}
                    maxLength={300}
                    showCount
                    placeholder={t('scene.aiAgentConfig.feedbackDialog.placeholder.suggestion')}
                  />
                )}
              </FormField>

              {/* 建议回复内容输入 */}
              <FormField<string>
                name="suggestedReply"
                label={
                  <span style={{ fontWeight: 500, fontSize: 14, color: '#232426' }}>
                    {t('scene.aiAgentConfig.feedbackDialog.label.suggestedReply')}
                  </span>
                }
                data-sid="fm-replyfield-09"
              >
                {({ field }) => (
                  <TextArea
                    data-sid="fm-replyinput-10"
                    {...field}
                    rows={4}
                    maxLength={500}
                    showCount
                    placeholder={t('scene.aiAgentConfig.feedbackDialog.placeholder.suggestedReply')}
                  />
                )}
              </FormField>
            </Form>
          )}
        </JustRightHeightModal>
      )}
    </Formik>
  );
}

export default FeedbackModal;
